import i18n from '@/plugins/vue-i18n';
import assortmentCanvases from '@enums/assortment-canvases';
import CheckpoinTypes from '@enums/checkpoint-types';
import menuLevel from '@enums/reporting-data-level';
import reportingMetrics from '@enums/reporting-metrics';
import reportingSections from '@enums/reporting-sections';
import to from 'await-to-js';
import axios from 'axios';
import {
  camelCase,
  cloneDeep,
  compact,
  concat,
  find,
  flatMap,
  flatten,
  get,
  groupBy,
  includes,
  intersectionWith,
  isEmpty,
  keyBy,
  map,
  merge,
  partition,
  pick,
  size,
  set,
  some,
  sortBy,
  upperFirst,
} from 'lodash';
import { defineStore } from 'pinia';
import { useFeature } from '@/composables/use-feature';
import { useWorkpackageScenarioSelectStore } from './workpackage-scenario-select';

export const useReportingStore = defineStore('reporting', {
  state: () => ({
    baselineIds: [],
    comparisonIds: [],
    comparison2Ids: [],

    isLoading: true,
    isSaving: false,
    isSidebarShown: true,
    isReportDataStale: false,
    reportsGenerated: 0,
    reportData: {},

    spacebreaks: [],
    canvases: [],
    checkpoints: [],
    storeClasses: [],
    clusters: [],

    loadingSubsection: {
      statsTable: false,
      statsChart: false,
      relativeShareChart: false,
      priceLadderChart: false,
      comparisonWaterfallTable: false,
      productsTable: false,
    },

    metricValue: reportingMetrics.metrics.sales,

    filters: {
      [reportingSections.stats]: [],
      [reportingSections.relativeShare]: [],
      [reportingSections.priceLadder]: [],
      [reportingSections.comparisonWaterfall]: [],
    },

    attributeValue: null,
    attributeValuesForSelectedScenarios: {},

    layouts: [],
    layoutsLoaded: false,
  }),

  getters: {
    canvasById() {
      return keyBy(this.canvases, '_id');
    },

    getLayouts() {
      return this.layouts;
    },

    checkpointsById() {
      return keyBy(this.checkpoints, '_id');
    },

    canvasesByCanvasIdCombined() {
      return keyBy(this.canvases, 'canvasIdCombined');
    },

    checkpointsByCanvasIdCombined() {
      return keyBy(this.checkpoints, 'canvasIdCombined');
    },

    checkpointsByScenarioId() {
      return groupBy(this.checkpoints, 'scenarioId');
    },

    storeClassesById() {
      // TODO strange that we have to flatten this, but the API returns an array of storeClasses
      return keyBy(flatten(map(this.storeClasses, 'storeClasses')), '_id');
    },

    clustersById() {
      // TODO strange that we have to flatten this, but the API returns an array of clusters
      return keyBy(flatten(map(this.clusters, 'clusters')), 'clusterId');
    },

    getSelectionFromId() {
      return _ids => {
        const { showLiveCanvasInReportingMenu } = useFeature();
        if (showLiveCanvasInReportingMenu.value) {
          // Need to check if the id is within the checkpoints or the canvases array. As the user could have
          // selected the 'live' version.
          return compact(
            map(
              concat([], _ids || []),
              id => get(this.checkpointsById, id) || get(this.canvasById, id)
            )
          );
        }
        return compact(map(concat([], _ids || []), id => get(this.checkpointsById, id)));
      };
    },

    /*
     * Is any of Base C1 or C2 an observed checkpoint
     */
    isAnySelectionObserved() {
      return some(
        flatten([this.baselineIds, this.comparisonIds, this.comparison2Ids]).map(id => {
          return (
            this.checkpointsById[id]?.checkpointMeta.type === CheckpoinTypes.observedAssortment
          );
        })
      );
    },

    getCanvasName() {
      return entry => {
        if (size(entry.description)) return entry.description;
        // Handle when the canvas storeclass was deleted but canvas is still available, this safety check on the 'name' should be deleted once storeclass uses dependency tree on AOV3-1191
        const storeClassName = get(
          this.storeClassesById[entry.storeClassId],
          'name',
          i18n.t('general.notAvailable')
        );

        const clusterName =
          entry.clusterId === null
            ? i18n.t('reportingPage.unclustered')
            : this.clustersById[entry.clusterId].clusterName;
        return `${storeClassName} - ${clusterName}`;
      };
    },

    /*
    Returns common attributes by name of attributes across scenarios. When there's is no comparison selected,
    all baseline scenario attributes are returned instead
    */
    getAttributesIntersection() {
      return (baselineIds, comparisonIds, comparison2Ids) => {
        const workpackageScenarioSelectStore = useWorkpackageScenarioSelectStore();

        const baseline = this.getSelectionFromId(baselineIds || this.baselineIds);
        const comparison = this.getSelectionFromId(comparisonIds || this.comparisonIds);
        const comparison2 = this.getSelectionFromId(comparison2Ids || this.comparison2Ids);

        if (!size(baseline)) return [];
        const baselineCustomAttributes = flatMap(baseline, b =>
          get(workpackageScenarioSelectStore.scenariosById, [
            b.scenarioId || b._id,
            'customAttributes',
          ])
        );
        if (!size(comparison)) return baselineCustomAttributes;
        const comparisonCustomAttributes = flatMap(comparison, c =>
          get(workpackageScenarioSelectStore.scenariosById, [
            c.scenarioId || c._id,
            'customAttributes',
          ])
        );

        const customAttributesToCompare = [
          cloneDeep(baselineCustomAttributes),
          comparisonCustomAttributes,
        ];
        if (size(comparison2)) {
          const comparison2CustomAttributes = flatMap(comparison2, c2 =>
            get(workpackageScenarioSelectStore.scenariosById, [
              c2.scenarioId || c2._id,
              'customAttributes',
            ])
          );
          customAttributesToCompare.push(comparison2CustomAttributes);
        }
        return intersectionWith(...customAttributesToCompare, (x, y) => {
          if (x.name !== y.name) return false;
          // Create new array of the possible attr ids as this is needed for applying attribute filters for products
          // when uusing multiple scenarios which may have the same attribute name but different ids.
          x.attributeIds = [x.id, y.id];
          return true;
        });
      };
    },

    scenarioIdsForSelection() {
      const workpackageScenarioSelectStore = useWorkpackageScenarioSelectStore();
      const baseline = this.getSelectionFromId(this.baselineIds);
      const comparison = this.getSelectionFromId(this.comparisonIds);
      const comparison2 = this.getSelectionFromId(this.comparison2Ids);

      if (!size(baseline)) return [];
      const baselineScenarioIds = map(baseline, b =>
        get(workpackageScenarioSelectStore.scenariosById, [b.scenarioId || b._id, '_id'])
      );
      if (!size(comparison)) return baselineScenarioIds;
      const comparisonScenarioIds = map(comparison, c =>
        get(workpackageScenarioSelectStore.scenariosById, [c.scenarioId || c._id, '_id'])
      );
      if (!size(comparison2)) return [...baselineScenarioIds, ...comparisonScenarioIds];
      const comparison2ScenarioIds = map(comparison2, c2 =>
        get(workpackageScenarioSelectStore.scenariosById, [c2.scenarioId || c2._id, '_id'])
      );
      return [...baselineScenarioIds, ...comparisonScenarioIds, ...comparison2ScenarioIds];
    },

    getFirstCommonAttribute() {
      return ({ baselineIds, comparisonIds, comparison2Ids }) => {
        const allAttributes = this.getAttributesIntersection(
          baselineIds,
          comparisonIds,
          comparison2Ids
        );

        return get(allAttributes, '0.id', null);
      };
    },

    getAttributeFromId() {
      const allAttributes = this.getAttributesIntersection();

      return find(allAttributes, { id: this.attributeValue });
    },

    allowedMetrics() {
      // Use a getter so that when called i18n is initialized
      // TODO: Add based on client config
      // Shrink is excluded here as it should only be available for some screens and only for Fresh workpackages.
      // Individual components must explicitly re-add Shrink as an option when needed.
      return map(reportingMetrics.metrics, metric => ({
        type: metric,
        text: i18n.t(`reportingPage.metrics.${metric}`),
      })).filter(m => m.type !== reportingMetrics.metrics.shrink);
    },

    enabledMetrics() {
      const workpackageScenarioSelectStore = useWorkpackageScenarioSelectStore();
      const workpackageScopes = map(workpackageScenarioSelectStore.selectedWorkpackages, w =>
        camelCase(w.fillInSelection)
      );
      const sizeFields = [
        reportingMetrics.metrics.linearSpace,
        reportingMetrics.metrics.cubicSpace,
        reportingMetrics.metrics.frontalSpace,
        reportingMetrics.metrics.horizontalSpace,
      ];

      return this.allowedMetrics.map(metric => ({
        ...metric,
        disabled: includes(sizeFields, metric.type)
          ? !includes(workpackageScopes, metric.type)
          : false,
      }));
    },

    getValuesForSelectedAttribute() {
      return ({ name }) => {
        if (name) {
          const selectableValues = this.attributeValuesForSelectedScenarios[name] || [];
          // map data to conform to {name: 'x' , id: 'x'} convention
          return selectableValues.map(x => ({ name: x, id: x }));
        }
        return [];
      };
    },

    // These are all the options users will have to use in the filters component
    filterOptions() {
      return [
        ...this.getAttributesIntersection(
          this.baselineIds,
          this.comparisonIds,
          this.comparison2Ids
        ).map(attr => {
          return {
            // Anything passed in this object will be passed to the getValuesForSelectedAttribute function as a param
            ...attr,
            type: assortmentCanvases.Filters.customAttributes,
            allowSelectMultiple: true,
            valuesGetter: this.getValuesForSelectedAttribute,
          };
        }),
      ];
    },
  },

  actions: {
    async fetchCanvases({ live, excludeObserved }) {
      const workpackageScenarioSelectStore = useWorkpackageScenarioSelectStore();
      const workpackageIds = workpackageScenarioSelectStore.selectedWorkpackageIds;
      if (!size(workpackageIds)) return [];

      const query = { live, excludeObserved };
      const params = {
        workpackageIds,
        where: query,
        pick: [
          'description',
          'scenarioId',
          'clusterId',
          'storeClassId',
          'checkpointMeta',
          'hasBeenForecast',
          'live',
          'isFinalCheckpoint',
        ],
      };

      const [err, res] = await to(
        axios.get('/api/assortment-canvases/scenarios/checkpoints', { params })
      );
      if (err) throw new Error(err.message);

      // Assign additional hierarchy keys to ensure they are displayed correctly in the reporting menu
      return map(res.data, c => ({
        ...c,
        menuLevel: live ? menuLevel.canvas : menuLevel.checkpoint,
        canvasIdCombined: `${c.scenarioId}-${c.storeClassId}-${c.clusterId}`,
        bundleId: get(
          workpackageScenarioSelectStore.parentHierachyIdsByScenarioId,
          [c.scenarioId, 'bundleId'],
          null
        ),
        workpackageId: get(
          workpackageScenarioSelectStore.parentHierachyIdsByScenarioId,
          [c.scenarioId, 'workpackageId'],
          null
        ),
      }));
    },

    triggerReportUpdate() {
      // Reset all report data so that the cache is cleared and it refetches
      this.reportData = {};
      // All reports charts watch this variable to determine when to re-fetch data
      this.reportsGenerated += 1;
    },

    updateReportData(report, data, cacheKey) {
      // Use the cacheKey if provided, otherwise fallback to report name
      const reportKey = cacheKey || report;
      set(this.reportData, reportKey, data);
    },

    async fetchStoreClasses() {
      const workpackageScenarioSelectStore = useWorkpackageScenarioSelectStore();
      const workpackageIds = workpackageScenarioSelectStore.selectedWorkpackageIds;
      if (!size(workpackageIds)) {
        this.setStoreClasses([]);
        return;
      }

      const params = {
        workpackageIds,
        pick: ['storeClasses.name', 'storeClasses._id'],
      };
      const [err, res] = await to(axios.get('/api/workpackages/scenarios/furniture', { params }));
      if (err) throw new Error(err.message);
      this.setStoreClasses(res.data);
    },

    async fetchClusters() {
      const workpackageScenarioSelectStore = useWorkpackageScenarioSelectStore();
      const workpackageIds = workpackageScenarioSelectStore.selectedWorkpackageIds;
      if (!size(workpackageIds)) {
        this.setClusters([]);
        return;
      }
      const params = {
        workpackageIds,
        pick: ['description', 'scenarioId', 'clusterId', 'storeClassId', 'name', 'clusters'],
      };

      const [err, res] = await to(axios.get('/api/workpackages/scenarios/clusters', { params }));
      if (err) throw new Error(err.message);
      this.setClusters(res.data);
    },

    async loadWorkpackagesData() {
      await this.fetchStoreClasses();
      await this.fetchClusters();

      const canvases = await this.fetchCanvases({ live: true });
      const checkpoints = await this.fetchCanvases({
        live: false,
        excludeObserved: false,
      });

      // use same sorting as canvas page
      const [unclusteredCanvases, otherCanvases] = partition(sortBy(canvases, ['clusterId']), {
        clusterId: null,
      });
      this.setCanvases(unclusteredCanvases.concat(otherCanvases));
      this.setCheckpoints(checkpoints);
    },

    async resetFilters({ baseline, comparison, comparison2 }) {
      const baselineData = this.getSelectionFromId(baseline);
      const comparisonData = this.getSelectionFromId(comparison);
      const comparison2Data = this.getSelectionFromId(comparison2);

      this.setSelection({
        field: 'baselineIds',
        value: size(baselineData) ? baseline : [],
      });
      this.setSelection({
        field: 'comparisonIds',
        value: size(comparisonData) ? comparison : [],
      });
      this.setSelection({
        field: 'comparison2Ids',
        value: size(comparison2Data) ? comparison2 : [],
      });
    },

    async fetchAttributeValuesForSelectedScenarios() {
      if (!this.scenarioIdsForSelection.length) return;
      const [err, result] = await to(
        axios.get('/api/scenario-products/uniqueAttributeValuesForScenarios', {
          params: { scenarioIds: this.scenarioIdsForSelection },
        })
      );
      if (err) throw new Error(err.message);
      this.setAttributeValuesForSelectedScenarios(result.data);
    },

    async fetchReportingData(params) {
      if (isEmpty(params) || isEmpty(params.section) || isEmpty(this.baselineIds)) return [];
      const { section, reportArea, ...otherParams } = params;
      const subSectionName = `${camelCase(section)}${upperFirst(reportArea)}`;
      this.setLoadingSubSection({ field: subSectionName, loading: true });
      const requestParams = merge(
        {},
        otherParams,
        pick(this, [
          'baselineIds',
          'comparisonIds',
          'comparison2Ids',
          'metricValue',
          'attributeValue',
        ]),
        { reportArea }
      );

      const [err, res] = await to(
        axios.get(`/api/reporting/${params.section}`, { params: requestParams })
      );
      this.setLoadingSubSection({ field: subSectionName, loading: false });
      if (err) return [];

      return res.data;
    },

    async fetchLayoutsData(force = false) {
      if (!force && this.layoutsLoaded) {
        return this.getLayouts;
      }
      const [err, res] = await to(axios.get('/api/reporting/layouts', {}));
      if (err) throw new Error(err.message);
      this.setLayouts(res.data);
      this.layoutsLoaded = true;

      return res.data;
    },

    async createLayout(params) {
      this.setIsSaving(true);
      const [err, res] = await to(axios.post('/api/reporting/layouts', params));
      if (err) {
        this.setIsSaving(false);
        throw new Error(err.message);
      }
      await this.fetchLayoutsData(true);
      this.setIsSaving(false);

      return res.data;
    },

    async deleteLayout({ layoutId }) {
      const [err] = await to(axios.delete(`/api/reporting/layouts/${layoutId}`));
      if (err) throw new Error(err.message);
      // commit('deleteLayout', layoutId);
      this.removeLayout(layoutId);
    },

    updateSidebarVisibility(newValue) {
      this.isSidebarShown = newValue;
    },

    setReportDataStale(isStale) {
      this.isReportDataStale = isStale;
    },

    setCanvases(canvases) {
      this.canvases = canvases;
    },

    setLayouts(layouts) {
      this.layouts = layouts;
    },

    removeLayout(layoutId) {
      this.layouts = this.layouts.filter(layout => layout._id !== layoutId);
    },

    setCheckpoints(checkpoints) {
      this.checkpoints = checkpoints;
    },

    setSelection({ field, value }) {
      this[field] = value;
    },

    setStoreClasses(storeClasses) {
      this.storeClasses = storeClasses;
    },

    setClusters(clusters) {
      this.clusters = clusters;
    },

    setIsLoading(isLoading) {
      this.isLoading = isLoading;
    },
    setIsSaving(isSaving) {
      this.isSaving = isSaving;
    },

    setLoadingSubSection({ field, loading }) {
      // Vue.set(state.loadingSubsection, field, loading);
      this.loadingSubsection[field] = loading;
    },

    resetReportingFilters() {
      this.filters = {
        [reportingSections.stats]: [],
        [reportingSections.relativeShare]: [],
        [reportingSections.priceLadder]: [],
        [reportingSections.comparisonWaterfall]: [],
      };
    },

    setReportingFilters({ section, filters }) {
      // Vue.set(state.filters, section, filters);
      this.filters[section] = filters;
    },

    setAttributeValuesForSelectedScenarios(attributeValuesForSelectedScenarios) {
      this.attributeValuesForSelectedScenarios = attributeValuesForSelectedScenarios;
    },
  },
});
